import * as React from 'react';
import type {
  GenericFieldHTMLAttributes,
  FieldMetaProps,
  FieldHelperProps,
  FieldInputProps,
  FieldConfig,
  FormikState,
} from './types';
import { useFormikMethods } from './FormikContext';
import { getIn, isObject } from './utils';
import invariant from 'tiny-warning';

export type FieldHookConfig<T> = GenericFieldHTMLAttributes & FieldConfig<T>;

interface FieldSlice {
  value: any;
  error: any;
  touched: boolean;
  params: any;
}

function selectFieldSlice(state: FormikState<any>, name: string): FieldSlice {
  return {
    value: getIn(state.values, name),
    error: getIn(state.errors, name),
    touched: !!getIn(state.touched, name),
    params: getIn(state.validationParams, name),
  };
}

function isSameSlice(a: FieldSlice, b: FieldSlice): boolean {
  return (
    a.value === b.value &&
    a.error === b.error &&
    a.touched === b.touched &&
    a.params === b.params
  );
}

/**
 * Subscribes to the formik store and re-renders only when this field's own
 * value / error / touched / params change. The slice is kept referentially
 * stable across unrelated updates so useSyncExternalStore can bail out.
 */
function useFieldSubscription(name: string): void {
  const { subscribe, getState } = useFormikMethods();
  const lastSlice = React.useRef<FieldSlice | null>(null);

  const getSnapshot = React.useCallback(() => {
    const next = selectFieldSlice(getState(), name);
    const last = lastSlice.current;
    if (last && isSameSlice(last, next)) {
      return last;
    }
    lastSlice.current = next;
    return next;
  }, [getState, name]);

  React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * useField hook - Connect a field to Formik
 *
 * Returns a tuple of [fieldProps, metaProps, helperProps]
 *
 * @example
 * const [field, meta, helpers] = useField('email');
 * return <input {...field} />;
 *
 * @example
 * const [field, meta] = useField({ name: 'email', validate: validateEmail });
 */
export function useField<Val = any>(
  propsOrFieldName: string | FieldHookConfig<Val>
): [FieldInputProps<Val>, FieldMetaProps<Val>, FieldHelperProps<Val>] {
  const {
    getFieldProps,
    getFieldMeta,
    getFieldHelpers,
    registerField,
    unregisterField,
  } = useFormikMethods();

  const isAnObject = isObject(propsOrFieldName);

  // Normalize propsOrFieldName to FieldHookConfig<Val>
  const props: FieldHookConfig<Val> = isAnObject
    ? (propsOrFieldName as FieldHookConfig<Val>)
    : { name: propsOrFieldName as string };

  const { name: fieldName, validate: validateFn } = props;

  invariant(
    fieldName,
    'Invalid field name. Either pass `useField` a string or an object containing a `name` key.'
  );

  useFieldSubscription(fieldName);

  React.useEffect(() => {
    if (fieldName) {
      registerField(fieldName, {
        validate: validateFn,
      });
    }
    return () => {
      if (fieldName) {
        unregisterField(fieldName);
      }
    };
  }, [registerField, unregisterField, fieldName, validateFn]);

  const fieldHelpers = React.useMemo(
    () => getFieldHelpers(fieldName),
    [getFieldHelpers, fieldName]
  );

  // The getters read the same store the subscription snapshot came from.
  return [getFieldProps(props), getFieldMeta(fieldName), fieldHelpers];
}
